import { i18n } from '@/plugins/vue-i18n.js'
import dayjs from 'dayjs'
import api from '@/plugins/api'
import { ACCOUNT_STATE } from '@/configs/constants'

export const setCookie = function (cname, cvalue, exdays) {
  const expires = 'expires=' + dayjs().add(exdays, 'day').toDate().toUTCString()
  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/'
}

export const setMessageDate = function (date, isMobile) {
  const dateSend = this.$dayjs(date)
  const minutes = this.$dayjs().diff(dateSend, 'minute')
  const minutesInDay = 24 * 60

  if (minutes <= 60) return isMobile ? dateSend.format('HH:mm') : this.$tc('minutes', minutes)
  else if (minutes > 60 && minutes <= minutesInDay) return dateSend.format('HH:mm')
  else return dateSend.format('YYYY.MM.DD')
}

export const formatAmount = (amount, options = {}) => {
  const { withCurrency = false, currency = 'UAH', locale = i18n.locale === 'ua' ? 'uk-UA' : 'ru-RU' } = options

  const normalizedAmount = Number(amount)
  const safeAmount = Number.isFinite(normalizedAmount) ? normalizedAmount : 0
  const formatOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }

  if (withCurrency) {
    formatOptions.style = 'currency'
    formatOptions.currency = currency
  }

  return new Intl.NumberFormat(locale, formatOptions).format(safeAmount)
}

export const calculateMonthlyPrice = dayFee => Math.round((dayFee || 0) * 30)

export const copyUrl = text => {
  try {
    navigator.clipboard.writeText(text)
  } catch (e) {
    throw e
  }
}

export const getLocaleKey = (source = {}, key) => {
  if (!key) return ''

  if (i18n.locale === 'ua') {
    if (`${key}_ukr` in source) return source[`${key}_ukr`]
    if (`${key}_uk` in source) return source[`${key}_uk`]
    return source[`${key}_ukr`]
  }

  if (`${key}_ru` in source) return source[`${key}_ru`]
  return source[key]
}

export const getDirectusImageLink = (url, params = { format: 'webp' }) => {
  return `${process.env.VUE_APP_DIRECTUS_URL}assets/${url}?${new URLSearchParams(params).toString()}`
}

export const formatMacAddress = input => {
  // Remove any non-hexadecimal characters
  const cleanInput = input.replace(/[^a-fA-F0-9]/g, '')

  // Ensure it's at most 12 characters long
  const trimmedInput = cleanInput.slice(0, 12)

  // Format into MAC address (XX:XX:XX:XX:XX:XX)
  return trimmedInput.match(/.{1,2}/g)?.join(':') || ''
}

export function debounce(fn, wait = 0) {
  let timerId = null
  let lastArgs = null
  let lastThis = null
  function invoke() {
    const args = lastArgs
    const context = lastThis
    lastArgs = null
    lastThis = null
    fn.apply(context, args || [])
  }

  function debounced(...args) {
    lastArgs = args
    lastThis = this
    if (timerId) clearTimeout(timerId)
    timerId = setTimeout(() => {
      timerId = null
      invoke()
    }, wait)
  }

  debounced.cancel = () => {
    if (timerId) clearTimeout(timerId)
    timerId = null
    lastArgs = null
    lastThis = null
  }

  debounced.flush = () => {
    if (!timerId) return
    clearTimeout(timerId)
    timerId = null
    invoke()
  }

  return debounced
}

export function extractErrors(value) {
  const result = []

  function walk(node) {
    if (typeof node === 'string') {
      result.push(node)
      return
    }

    if (Array.isArray(node)) {
      for (const item of node) {
        walk(item)
      }
      return
    }

    if (node && typeof node === 'object') {
      for (const key in node) {
        walk(node[key])
      }
    }
  }

  walk(value)
  return result
}

export const blobToObjectUrl = blob =>
  window.URL.createObjectURL(new File([blob], 'file_name', { lastModified: new Date(), type: blob.type }))

export const withPhotoFile = async item => {
  if (!item?.photo) return null
  try {
    const response = await api.get(item.photo, { responseType: 'blob' })
    if (![200, 201].includes(response.code)) return null
    // Guard against HTML (SPA fallback / error pages) served as 200 being wrapped into a broken
    // <img> blob — e.g. a media host returning index.html for a missing file.
    if (response.data?.type?.startsWith('text/html')) return null
    return { ...item, file: blobToObjectUrl(response.data) }
  } catch (error) {
    console.warn(`withPhotoFile: failed to load ${item.photo}`, error)
    return null
  }
}

export const composeFullName = ({ last_name, first_name, middle_name } = {}) =>
  [last_name, first_name, middle_name].filter(Boolean).join(' ')

export const composeAddress = ({ city, street, build, flat } = {}) => [city, street, build, flat].filter(Boolean).join(', ')

export const getAccountStateBadge = (status, t) => {
  switch (status) {
    case ACCOUNT_STATE.ACTIVE:
      return { color: 'color-orange', text: t('dashBoards.active') }
    case ACCOUNT_STATE.FREEZE:
      return { color: 'color-blue', text: t('dashBoards.freeze') }
    case ACCOUNT_STATE.SMALL_DEPOSIT:
      return { color: 'color-black', text: t('dashBoards.small_deposit') }
    case ACCOUNT_STATE.BLOCKED:
      return { color: 'color-red', text: t('tvStatuses.14') }
    case null:
      return { color: 'color-red', text: t('dashBoards.notActive') }
    default:
      return { color: 'color-orange', text: '-' }
  }
}

export function formatPeriod(year, month) {
  if (month == null) {
    return i18n.t('reportPeriod.year', { year })
  }

  if (!Number.isInteger(month) || month < 1 || month > 12) {
    throw new Error('Month must be an integer from 1 to 12')
  }

  const localeMap = {
    ua: 'uk-UA',
    ru: 'ru-RU',
    default: 'uk-UA'
  }

  const monthName = new Intl.DateTimeFormat(localeMap[i18n.locale] || localeMap.default, {
    month: 'long'
  }).format(dayjs(`2000-${String(month).padStart(2, '0')}-01`).toDate())

  return i18n.t('reportPeriod.monthYear', {
    month: monthName,
    year
  })
}
